<template>
  <div class="select base-input" @focusout="blurHandler" ref="select" tabindex="0">
    <div class="select-block">
      <label v-if="label" class="input__label" @click="toggleDialog">
        {{ label }}{{ required ? '*' : '' }}
      </label>

      <!-- readonly -->
      <div v-if="readonly" class="readonly">
        <slot v-for="item in inputValue" name="item" :item="item">
          {{ item }}
        </slot>
      </div>

      <!-- selectable -->
      <div class="selectable-content">
        <!-- field -->
        <div 
          :class="['select-field', { error: hasValidationError }]"
          @click.stop="toggleDialog"
        >
          <div v-if="shownSelected">
            <slot v-if="shownSelected !== placeholder" name="item" :item="shownSelected">
              {{ shownSelected }}
            </slot>
            <span v-else>{{ placeholder }}</span>
          </div>
          <span class="icons__control">
            <svg width="15" height="10" viewBox="0 0 15 10" fill="none" xmlns="http://www.w3.org/2000/svg" :class="['dropdown_icon', { active: selectDialog }]" style="margin-left: 10px">
              <path d="M1 1L7 7L13 1" stroke="#0E619F" stroke-width="2" />
            </svg>
          </span>
        </div>

        <!-- error message -->
         <slot v-if="hasValidationError" />

        <!-- dialog-window -->
        <transition :duration="200" name="select">
          <div v-show="selectDialog" ref="selectDialog" class="selectDialog">
            <div class="selectDialog__search">
              <SearchForm
                v-model="searchValue"
                :disabled='isSelectedAndIsSingle'
                @form-submit="searchItems"
              />
            </div>
            <div class="selectDialog__list">
              <span v-for="(item, index) in shownItems"
                :key="index"
                @click.stop="changeSelected(item)"
                class='item'
              >
                <Checkbox
                  :disabled='isSelectedAndIsSingle'
                  :value="isSelected(item)"
                  :class='isSingle ?  "is-hide" : ""'
                />
                <slot name="item" :item="item">
                  <span class="item-coll">
                    <span class="item__name">{{ item['job_type'] }}</span>
                    <span class="item__text">{{ item['description'] }}</span>
                    <span class="item__name">{{ item['executor'] }}</span>
                  </span>
                </slot>
              </span>
              <div v-if="!shownItems || !shownItems.length" class="item item--not">
                нет вариантов
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>

    <AppPageHeader
      v-if="jobList.length"
      title="Работы в заявке"
      class="mb-20"
    />

    <DropdownJobList
      :job-list="jobList"
      @onRemove="removeJob"
    />
  </div>
</template>

<script>
import Vue from 'vue'
import Base from './Vinput/Base/Base.vue'
import Checkbox from './Checkbox.vue'
import DropdownJobList from '@/components/pto/Job/DropdownJobList'
import AppPageHeader from '@/components/page/AppPageHeader'
import SearchForm from '@/components/page/input/SearchForm'
import { mixinSelect } from '@/mixins/form/mixinSelect'

export default {
  name: 'VSelectJob',
  components: {
    Checkbox,
    DropdownJobList,
    AppPageHeader,
    SearchForm
  },
  props: {
    // off MULTI select
    isSingle: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  mixins: [mixinSelect],
  extends: Base,
  model: {
    event: 'change'
  },
  data () {
    return {
      defaultValue: []
    }
  },
  computed: {
    inputValue: {
      get () {
        if (this.formData && this.name) return this.formData[this.name] || this.defaultValue
        else return this.defaultValue
      },
      set (val) {
        if (this.formData && this.name) Vue.set(this.formData, this.name, val)
        this.$emit('change', val)
      }
    },
    isSelectedAndIsSingle () {
      return !!(this.isSingle && this.inputValue.length)
    },
    shownSelected () {
      return this.inputValue?.length ? `выбрано: ${this.inputValue.length}` : this.placeholder
    },
    jobList () {
      return this.inputValue
    }
  },
  methods: {
    toggleDialog () {
      if (!this.loading && !this.readonly) this.selectDialog = !this.selectDialog
    },
    isSelected (item) {
      if (!this.inputValue) return false
      if (Array.isArray(this.inputValue)) return this.inputValue.some(el => el.id === item.id)
      else return this.inputValue === item
    },
    changeSelected (item) {
      let copy = this.inputValue
      if (copy.find((el) => el.id === item.id)) copy = copy.filter(el => el.id !== item.id)
      //  add check single choice
      else if (!this.isSelectedAndIsSingle) copy = [...copy, item]
      if (this.inputValue !== copy) this.inputValue = copy
    },
    blurHandler (event) {
      const enteringParent = this.$refs.select.contains(event.relatedTarget)
      if (!enteringParent) {
        this.selectDialog = false
        this.searchValue = ''
      }
    },
    removeJob (currentJob) {
      this.inputValue = this.inputValue.filter(item => item.id !== currentJob)
    },
    searchFront () {
      if (this.searchValue) {
        const search = this.searchValue.toLowerCase()

        this.shownItems = this.items.filter(item => {
          const itemName = item.job_type.toLowerCase()
          return itemName.includes(search)
        })
      } else this.shownItems = this.items
    }
  }
}
</script>

<style lang="scss" scoped>
  .readonly {
    display: flex;
    font-size: 16px;
    margin-top: 4px
  }
  .select-enter, .select-leave-to {
    transform: translateY(5px);
    opacity: 0;
  }
  .select-enter-active, .select-leave-active {
    transition: all 0.2s;
  }
  .select-enter-to, .select-leave {
    transform: translateY(0);
    opacity: 1;
  }
  .select {
    position: relative;
    outline: none;
    text-align: left;
    .dropdown_icon {
      stroke: $color_info;
      transition: transform 0.2s;
      &.active {
        transform: rotate(180deg);
      }
    }
  }
  .select-block {
    width: 100%;
  }
  .selectable-content {
    margin-top: 10px;
  }
  .select-field {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: 40px;
    max-height: 142px;
    max-width: 100%;
    font-size: $font_main_size;
    color: $color_third-dark;
    transition: all 0.2s;
    border: 1px solid $color_third-dark;
    background: white;
    border-radius: 4px;
    padding: 0 13px;
    outline: none;
    box-sizing: border-box;
    cursor: pointer;

    input::placeholder {
      color: #b8cad5;
      font-size: 16px;
    }
  }
  .selectDialog {
    display: flex;
    flex-direction: column;
    position: absolute;
    background: white;
    box-shadow: 0 10px 20px rgba(38, 40, 135, 0.12), 0 2px 6px rgba(0, 0, 0, 0.04), 0 0 1px rgba(0, 0, 0, 0.04);
    overflow: auto;
    width: 100%;
    animation: showing 0.2s linear;
    border-radius: 4px;
    z-index: 1000;
    padding: 21px;

    &__list {
      max-height: 264px;
      overflow: auto;

      &::-webkit-scrollbar {
        width: 7px;
        background-color: #F3F5FF;
      }

      &::-webkit-scrollbar-thumb {
        background-color: #1170B8;
      }
    }

    &__search {
      margin-bottom: 20px;
    }

    @keyframes showing {
      0% {
        opacity: 0;
      }

      100% {
        opacity: 1;
      }
    }
    .item-coll {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: start;
      width: 100%;
    }

    .item {
      display: flex;
      cursor: pointer;
      border-top: 1px solid #CBCBCB;
      padding: 16px 12px;
      font-weight: 400;
      font-size: 14px;
      line-height: 17px;
      margin-right: 18px;

      &:first-child {
        border: none;
      }

      &:hover {
        background-color: transparentize($color_second-light, .8);
      }

      &__name {
        margin-bottom: 4px;
        color: #000000;
      }

      &__text {
        color: #697178;
        margin-bottom: 4px;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
      }

      &--not {
        pointer-events: none;
        margin: 0 auto;
      }
    }
  }
  .checkbox {
    padding-right: 15px;
  }
  .icons__control {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    min-width: 40px;
  }
  .is-hide {
    display: none;
  }

</style>
